<template>
    <div class="file-question">
        <label><slot name="label"></slot></label>

        <div
            class="dropzone"
            :class="{ dragging: dragging, disabled: !canAddMore }"
            @dragenter.prevent="dragging = true"
            @dragover.prevent="dragging = true"
            @dragleave.prevent="dragging = false"
            @drop.prevent="onDrop"
            @click="openPicker"
        >
            <span class="dropzone-icon">&#8593;</span>
            <span class="dropzone-title">{{ t('question-file_drop') }}</span>
            <span class="dropzone-hint">{{ hint }}</span>
            <input
                ref="fileInput"
                type="file"
                :accept="accept"
                :multiple="multiple"
                @change="onPick"
            />
        </div>

        <div class="uploading" v-if="uploading">{{ t('question-file_uploading') }}</div>

        <ul class="file-list" v-if="files.length">
            <li
                v-for="(file, index) in files"
                :key="fileKey(file, index)"
                :class="{ 'has-preview': hasPreview(file, index) }"
            >
                <a
                    v-if="hasPreview(file, index)"
                    class="thumb"
                    :href="file.url"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <img
                        :src="previewSrc(file, index)"
                        :alt="file.name"
                        @error="previewFailed(file, index)"
                    />
                </a>
                <span v-else class="file-chip">{{ extensionLabel(file) }}</span>
                <a
                    class="file-name"
                    :href="file.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    >{{ file.name }}</a
                >
                <button
                    type="button"
                    class="remove"
                    @click.prevent.stop="remove(index)"
                >
                    &times;
                </button>
            </li>
        </ul>

        <div class="error" v-if="error">{{ error }}</div>
    </div>
</template>
<script>
import keviApi from "../../keviApi";

const MAX_SIZE_MB = 30;
const ALL_EXTENSIONS = [
    "jpg", "jpeg", "png", "gif", "webp", "bmp", "tif", "tiff",
    "heic", "heif", "avif", "ico", "pdf", "doc", "docx", "xls",
    "xlsx", "ppt", "pptx", "odt", "ods", "odp", "rtf", "txt", "csv",
    "dst", "zip", "rar", "7z", "gz", "mp3", "wav", "ogg", "m4a",
    "aac", "mp4", "mov", "avi", "webm", "mkv",
];
const IMAGE_EXTENSIONS = [
    "jpg", "jpeg", "png", "gif", "webp", "bmp", "tif", "tiff",
    "heic", "heif", "avif", "ico",
];
// Formats every browser can draw in an <img>. HEIC/TIFF are accepted as
// uploads but most browsers cannot render them, so they keep the file chip.
const PREVIEW_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "avif", "ico"];

function extensionOf(name) {
    const parts = String(name || "").split(".");
    return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

export default {
    name: "file-question",
    props: {
        formId: [String, Number],
        questionId: [String, Number],
        linkCode: String,
        settings: {
            type: Object,
            default: function () {
                return {};
            },
        },
    },
    data: function () {
        return {
            files: [],
            uploading: false,
            dragging: false,
            error: null,
            // Local object urls of the files picked on this device, keyed by
            // fileKey: the thumbnail shows instantly and without depending on
            // the signed link. Kept out of `files` so it is never submitted.
            localPreviews: {},
            failedPreviews: {},
        };
    },
    // Vue 2: beforeUnmount does not exist here.
    // eslint-disable-next-line vue/no-deprecated-destroyed-lifecycle
    beforeDestroy: function () {
        Object.keys(this.localPreviews).forEach((key) => {
            this.revokePreview(key);
        });
    },
    computed: {
        multiple: function () {
            return this.settings && this.settings.multiple !== false;
        },
        maxFiles: function () {
            if (!this.multiple) {
                return 1;
            }
            const configured = Number(this.settings && this.settings.maxFiles) || 30;
            return Math.min(30, Math.max(1, configured));
        },
        canAddMore: function () {
            return this.files.length < this.maxFiles;
        },
        accept: function () {
            return this.allowedExtensions.map((ext) => "." + ext).join(",");
        },
        allowedExtensions: function () {
            const acceptedTypes =
                (this.settings && this.settings.acceptedTypes) || ["any"];
            if (acceptedTypes.indexOf("any") !== -1) {
                return ALL_EXTENSIONS;
            }
            const extensions = [];
            if (acceptedTypes.indexOf("image") !== -1) {
                extensions.push(...IMAGE_EXTENSIONS);
            }
            if (acceptedTypes.indexOf("dst") !== -1) {
                extensions.push("dst");
            }
            return Array.from(new Set(extensions));
        },
        hint: function () {
            return this.t("question-file_hint", {
                types: this.acceptedTypeLabel,
                size: MAX_SIZE_MB,
                max: this.maxFiles,
            });
        },
        acceptedTypeLabel: function () {
            const acceptedTypes =
                (this.settings && this.settings.acceptedTypes) || ["any"];
            if (acceptedTypes.indexOf("any") !== -1) {
                return this.t("question-file_types_any");
            }
            if (
                acceptedTypes.indexOf("image") !== -1 &&
                acceptedTypes.indexOf("dst") !== -1
            ) {
                return this.t("question-file_types_image_dst");
            }
            return acceptedTypes.indexOf("dst") !== -1
                ? this.t("question-file_types_dst")
                : this.t("question-file_types_image");
        },
    },
    methods: {
        openPicker: function () {
            if (!this.canAddMore || this.uploading) {
                return;
            }
            this.$refs.fileInput.click();
        },
        onPick: function (event) {
            this.upload(Array.from(event.target.files || []));
            // Allows picking the same file again after removing it.
            event.target.value = "";
        },
        onDrop: function (event) {
            this.dragging = false;
            this.upload(Array.from(event.dataTransfer.files || []));
        },
        validate: function (file) {
            const extension = (file.name.split(".").pop() || "").toLowerCase();
            if (this.allowedExtensions.indexOf(extension) === -1) {
                return this.t("question-file_invalid_type", { name: file.name });
            }
            if (file.size / 1024 / 1024 > MAX_SIZE_MB) {
                return this.t("question-file_too_large", {
                    name: file.name,
                    size: MAX_SIZE_MB,
                });
            }
            return null;
        },
        upload: function (selected) {
            if (!selected.length || this.uploading) {
                return;
            }

            this.error = null;
            const room = this.maxFiles - this.files.length;
            const accepted = [];

            for (const file of selected.slice(0, room)) {
                const validationError = this.validate(file);
                if (validationError) {
                    this.error = validationError;
                    continue;
                }
                accepted.push(file);
            }

            if (selected.length > room) {
                this.error = this.t("question-file_too_many", {
                    max: this.maxFiles,
                });
            }

            if (!accepted.length) {
                return;
            }

            const pending = accepted.map((file) => ({
                name: file.name,
                preview: this.createLocalPreview(file),
            }));

            const formData = new FormData();
            accepted.forEach((file) => formData.append("files[]", file));
            if (this.linkCode) {
                formData.append("link_code", this.linkCode);
            }
            if (this.questionId) {
                formData.append("question_id", this.questionId);
            }

            this.uploading = true;
            const options = keviApi.getOptions(
                "form/" + this.formId + "/upload",
                "POST",
            );

            this.$http
                .post(options.url, formData)
                .then((response) => {
                    const uploaded = (response.body && response.body.files) || [];
                    const offset = this.files.length;
                    uploaded.forEach((file, i) => {
                        const match = pending.find(
                            (item) => !item.used && item.name === file.name,
                        );
                        if (match && match.preview) {
                            match.used = true;
                            this.$set(
                                this.localPreviews,
                                this.fileKey(file, offset + i),
                                match.preview,
                            );
                        }
                    });
                    this.files = this.files.concat(uploaded);
                    this.$emit("change", this.getValue());
                })
                .catch(() => {
                    this.error = this.t("question-file_upload_error");
                })
                .finally(() => {
                    this.uploading = false;
                    pending
                        .filter((item) => item.preview && !item.used)
                        .forEach((item) => URL.revokeObjectURL(item.preview));
                });
        },
        remove: function (index) {
            this.revokePreview(this.fileKey(this.files[index], index));
            this.files.splice(index, 1);
            this.$emit("change", this.getValue());
        },
        fileKey: function (file, index) {
            return (file && (file.storage_ref || file.url)) || "file-" + index;
        },
        isPreviewable: function (file) {
            if (!file) {
                return false;
            }
            return PREVIEW_EXTENSIONS.indexOf(extensionOf(file.name)) !== -1;
        },
        hasPreview: function (file, index) {
            return (
                this.isPreviewable(file) &&
                !this.failedPreviews[this.fileKey(file, index)] &&
                !!this.previewSrc(file, index)
            );
        },
        previewSrc: function (file, index) {
            return this.localPreviews[this.fileKey(file, index)] || file.url;
        },
        previewFailed: function (file, index) {
            this.$set(this.failedPreviews, this.fileKey(file, index), true);
        },
        extensionLabel: function (file) {
            return extensionOf(file && file.name).toUpperCase() || "FILE";
        },
        createLocalPreview: function (file) {
            if (
                PREVIEW_EXTENSIONS.indexOf(extensionOf(file.name)) === -1 ||
                typeof URL === "undefined" ||
                !URL.createObjectURL
            ) {
                return null;
            }
            return URL.createObjectURL(file);
        },
        revokePreview: function (key) {
            const preview = this.localPreviews[key];
            if (preview) {
                URL.revokeObjectURL(preview);
            }
            this.$delete(this.localPreviews, key);
            this.$delete(this.failedPreviews, key);
        },
        focus: function () {
            this.$emit("focus", this);
        },
        getValue: function () {
            return this.files;
        },
        isValid: function () {
            return !this.uploading;
        },
        isNull: function () {
            return this.files.length === 0;
        },
    },
};
</script>
